import { el } from "redom"
import { Audio } from "./audioplayer.js"
import type { EvalChartMetadata } from "#/types.js"
import { getTimeAgo } from "../helpers/time-converter.js"

const getContext = (id: string) => (<HTMLCanvasElement>document.getElementById(id))?.getContext('2d')
export const proficiencyTrendChartId: string = "proficiencyTrendChart"
export const loadProficiencyTrendChart = () => {
    const ctx = getContext(proficiencyTrendChartId)
    if (ctx == undefined) return null
    return new Chart(ctx, {
        // The type of chart we want to create
        type: 'line',

        // The data for our dataset
        data: {
            labels: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
            datasets: [{
                label: 'graph dataset',
                borderColor: 'rgb(124,183,222)',
                backgroundColor: 'rgb(214,233,245)',
                pointBorderWidth: 5,
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                cubicInterpolationMode: 'monotone',
                fill: true,
                spanGaps: true,
                steppedLine: false,
            }]
        },

        // Configuration options go here
        options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 10,
                    bottom: 10,
                },
            },
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            tooltips: {
                enabled: false,
            },
            scales: {
                xAxes: [{
                    gridLines: {
                        display: true,
                    },
                    ticks: {
                        display: true,
                        fontSize: 12,
                        maxRotation: 90,
                        minRotation: 90,
                    },
                }],
                yAxes: [{
                    gridLines: {
                        display: false,
                    },
                    ticks: {
                        display: false,
                    },
                }],
            }
        }
    })
}

const evalChartId: string = "scChart"
const evalChartStatusIconId = "scChartStatusIcon"
const evalChartStatusTextId = "scChartStatusText"
const evalChartScoreId = "scChartScore"
const evalChartUnevaluatedId = "scChartUnevaluated"
const evalChartHeight = 120 // Matches Android RecordUI evaluationBoxHeight.
const progressOverlayColor = "rgba(0, 0, 0, 0.3)"
const maxHue = 285
const SAT_SCORE_CEILING = 0.4 // sat score over this number will be considered as saturation value 0.0 (uncolored)

// Diagonal stripes for unevaluated parts of the recording
const unevaluatedPattern = (ctx: CanvasRenderingContext2D): CanvasPattern | null => {
    const size = Math.round(8 * (window.devicePixelRatio || 1))
    const tile = document.createElement("canvas")
    tile.width = tile.height = size
    const tileCtx = tile.getContext("2d")
    if (!tileCtx) return null
    tileCtx.strokeStyle = "rgba(255, 74, 74, 0.55)"
    tileCtx.lineWidth = Math.max(size / 3, 1)
    tileCtx.beginPath()
    for (const offset of [0, size, size * 2]) {
        tileCtx.moveTo(offset - size, size)
        tileCtx.lineTo(offset, 0)
    }
    tileCtx.stroke()
    return ctx.createPattern(tile, "repeat")
}

interface EvalLevel {
    hue: number
    iconSrc: string
    text: string
    textColor: string
}

interface EvalChartPoint {
    hue: number
    saturation: number
}

interface EvalChartState {
    canvas: HTMLCanvasElement
    offscreenCanvas: HTMLCanvasElement
    chartData: number[][]
    chartLength: number
    chartMetadata: EvalChartMetadata
    lastDisplayedLevel?: EvalLevel
    showingUnevaluated?: boolean
    durationSeconds: number
    timeElapsed: number
    onTimeUpdate: () => void
    onPointerDown: (evt: PointerEvent) => void
}

let evalChartState: EvalChartState | undefined

const evalLevels: EvalLevel[] = [
    // Keep hue values aligned with EvaluatedAitutorSeq.scoreWeights in Model.scala.
    { hue: 5, iconSrc: "/public/img/ic_aitutor_face0.svg", text: "???", textColor: "#666666" },
    { hue: 15, iconSrc: "/public/img/ic_aitutor_face1.svg", text: "Need\nmore practice", textColor: "#ff4000" },
    { hue: 30, iconSrc: "/public/img/ic_aitutor_face2.svg", text: "Can do better", textColor: "#ff8001" },
    { hue: 45, iconSrc: "/public/img/ic_aitutor_face3.svg", text: "Almost there", textColor: "#ffc000" },
    { hue: 180, iconSrc: "/public/img/ic_aitutor_face4.svg", text: "Good!", textColor: "#00ffff" },
    { hue: 195, iconSrc: "/public/img/ic_aitutor_face5.svg", text: "Great!", textColor: "#00bfff" },
    { hue: 285, iconSrc: "/public/img/ic_aitutor_face6.svg", text: "Perfect!", textColor: "#c000ff" },
]

const noMusicLevel: EvalLevel = { hue: 0, iconSrc: "/public/img/ic_aitutor_face0.svg", text: "No music", textColor: "#ffffff" }

const evalChartStatus = () =>
    el(".sc-eval-chart-status",
        el(`#${evalChartScoreId}.sc-eval-chart-score`,
            el(`img#${evalChartStatusIconId}.sc-eval-chart-status-icon`, { src: noMusicLevel.iconSrc, alt: "" }),
            el(`#${evalChartStatusTextId}.sc-eval-chart-status-text`, noMusicLevel.text)),
        el(`#${evalChartUnevaluatedId}.sc-eval-chart-unevaluated`, { hidden: true },
            el(".sc-eval-chart-unevaluated-message", "This is a preview of the AI Tutor evaluation. You can see the full evaluation in ‘Records on Device’ section of Soundcorset app.")
        )
    )

const evalChartInfo = () =>
    el("div", { style: "height: 22px; text-align: right;" },
        el("a#popoverBtnGraph.modalBtn", { style: "font-size: 16px;", tabindex: "0", "data-trigger": "focus", "data-bs-toggle": "popover", "data-bs-content": "This chart shows the proficiency of the musical instrument being played. The recording should be long enough in order to be evaluated by our artificial intelligence system.", "data-bs-placement": "left" },
            el("i.fas fa-info-circle clickable", { style: "color: #8a8e94" })))

const evalChart = () =>
    el(".sc-eval-chart", evalChartStatus(), el(".chart-container.sc-eval-chart-container", el(`canvas#${evalChartId}.sc-eval-chart-canvas`, { role: "img" })))

const membershipUpsellChart = () =>
    el(".sc-eval-chart-membership-preview",
        el(".sc-eval-chart-membership-preview-chart", { "aria-hidden": "true" },
            el(".sc-eval-chart-membership-placeholder",
                el(".sc-eval-chart-membership-placeholder-status"),
                el(".sc-eval-chart-membership-placeholder-timeline"))),
        el(".sc-eval-chart-membership-overlay",
            el(".sc-eval-chart-membership-title", "Your AI Tutor evaluation is ready"),
            el(".sc-eval-chart-membership-message", "The full evaluation chart is available to members"),
            el("button.btn sc_membership_button_rev rounded-pill sc-eval-chart-membership-button", {
                type: "button",
                onclick: () => window.location.href = "/public/purchase.html?backToPrev"
            }, "JOIN MEMBERSHIP")))

const evaluateNowCard = (evaluationAvailableAt: number) =>
    el(".card", { style: "background: #eeeeee; border: 0px; margin: 5px 5px 10px 5px; padding: 10px 20px 10px 20px; text-align: center; color: #666666;" },
        el("h6", { style: "font-weight: 400;" },
            "This record will be evaluated ", el("span", getTimeAgo(evaluationAvailableAt)), "."),
        el("button#evaluateButton.btn btn btn-lg sc_membership_button", { "data-trigger": "focus", "data-bs-toggle": "popover", title: "", "data-bs-content": "<a href='/public/purchase.html?backToPrev'><b><u>Get the tools</u></b> you need to improve your skills and share your music.</a>", "data-bs-placement": "top", "data-original-title": "Become a member!" }, "EVALUATE NOW"))

export const buildTimeSeriesEvaluatedScoreChart = (shouldShowEvaluatedChart: boolean, shouldShowMembershipUpsell: boolean, shouldShowEvaluateNow: boolean, evaluationAvailableAt: number) =>
    el("div", { style: "padding: 0px;" },
        shouldShowEvaluatedChart || shouldShowMembershipUpsell
            ? [evalChartInfo(), shouldShowEvaluatedChart ? evalChart() : membershipUpsellChart()]
            : shouldShowEvaluateNow ? evaluateNowCard(evaluationAvailableAt) : {})


const syncCanvasSize = (canvas: HTMLCanvasElement) => {
    const pixelRatio = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    const width = Math.max(Math.floor(rect.width || canvas.parentElement?.clientWidth || 0), 1)
    const height = Math.max(Math.floor(rect.height || evalChartHeight), 1)
    const targetWidth = Math.floor(width * pixelRatio)
    const targetHeight = Math.floor(height * pixelRatio)
    if (canvas.width != targetWidth) canvas.width = targetWidth
    if (canvas.height != targetHeight) canvas.height = targetHeight
    return { width: canvas.width, height: canvas.height }
}

const getEvalChartPoint = (state: EvalChartState, index: number): EvalChartPoint => {
    const scores = state.chartData
    const hueScores = scores[0] ?? []
    const satScores = scores[1] ?? []
    const hasSatScores = satScores.length > 0
    const hue = (hueScores[index] ?? 0) * maxHue
    const sat = !hasSatScores || satScores[index] == undefined
        ? 50
        : Math.floor((1 - Math.pow(Math.min(satScores[index], SAT_SCORE_CEILING) / SAT_SCORE_CEILING, 2)) * 100)
    return { hue, saturation: sat }
}

const getLevelForEvalChartPoint = (point: EvalChartPoint): EvalLevel => {
    if (point.saturation <= 0) return noMusicLevel
    return evalLevels.reduce((closest, level) =>
        Math.abs(level.hue - point.hue) < Math.abs(closest.hue - point.hue) ? level : closest)
}

const getProgress = (state: EvalChartState): number =>
    state.durationSeconds > 0 ? Math.max(Math.min(state.timeElapsed / state.durationSeconds, 1), 0) : 0

const updateEvalChartStatus = (state: EvalChartState) => {
    const showUnevaluated = state.timeElapsed >= state.chartMetadata.evaluatedUntilSeconds
    if (showUnevaluated != state.showingUnevaluated) {
        state.showingUnevaluated = showUnevaluated
        const scoreEl = document.getElementById(evalChartScoreId)
        const unevaluatedEl = document.getElementById(evalChartUnevaluatedId)
        if (scoreEl) scoreEl.hidden = showUnevaluated
        if (unevaluatedEl) unevaluatedEl.hidden = !showUnevaluated
    }
    if (showUnevaluated) return

    const dataIndex = Math.max(Math.min(Math.floor(state.timeElapsed / state.chartMetadata.hopSec), state.chartLength - 1), 0)
    const level = getLevelForEvalChartPoint(getEvalChartPoint(state, dataIndex))
    if (level == state.lastDisplayedLevel) return
    state.lastDisplayedLevel = level

    const iconEl = document.getElementById(evalChartStatusIconId)
    const textEl = document.getElementById(evalChartStatusTextId)
    if (iconEl instanceof HTMLImageElement) iconEl.src = level.iconSrc
    if (textEl) {
        textEl.textContent = level.text
        textEl.style.color = level.textColor
    }
}

const syncOffscreenChart = (state: EvalChartState, width: number, height: number) => {
    if (state.offscreenCanvas.width == width && state.offscreenCanvas.height == height) return
    state.offscreenCanvas.width = width
    state.offscreenCanvas.height = height
    const ctx = state.offscreenCanvas.getContext("2d")
    if (!ctx) return

    const timeline = state.durationSeconds > 0 ? state.durationSeconds : state.chartMetadata.evaluatedUntilSeconds
    const toX = (seconds: number) => Math.min(seconds, timeline) / timeline * width

    for (let i = 0; i < state.chartLength; i++) {
        const startSeconds = state.chartMetadata.hopSec * i
        if (startSeconds >= timeline) break
        const endSeconds = i == state.chartLength - 1 ? startSeconds + state.chartMetadata.inferenceSec : state.chartMetadata.hopSec * (i + 1)
        const left = Math.floor(toX(startSeconds))
        const point = getEvalChartPoint(state, i)
        ctx.fillStyle = `hsl(${point.hue},${point.saturation}%,${100 - point.saturation / 2}%)`
        ctx.fillRect(left, 0, Math.max(Math.ceil(toX(endSeconds)) - left, 1), height)
    }

    const evaluatedX = Math.floor(toX(state.chartMetadata.evaluatedUntilSeconds))
    const pattern = evaluatedX < width ? unevaluatedPattern(ctx) : null
    if (pattern) {
        ctx.fillStyle = pattern
        ctx.fillRect(evaluatedX, 0, width - evaluatedX, height)
    }
}

const drawEvalChart = (state: EvalChartState) => {
    const ctx = state.canvas.getContext("2d")
    if (!ctx) return

    updateEvalChartStatus(state)

    const { width, height } = syncCanvasSize(state.canvas)
    ctx.clearRect(0, 0, width, height)

    syncOffscreenChart(state, width, height)
    ctx.drawImage(state.offscreenCanvas, 0, 0, width, height)

    if (state.durationSeconds <= 0) return
    ctx.fillStyle = progressOverlayColor
    ctx.fillRect(0, 0, width * getProgress(state), height)
}

const cleanupEvalChart = () => {
    if (!evalChartState) return
    Audio.player?.media?.removeEventListener("timeupdate", evalChartState.onTimeUpdate)
    evalChartState.canvas.removeEventListener("pointerdown", evalChartState.onPointerDown)
    evalChartState = undefined
}

const renderEvalChartProgress = (timeElapsed: number = 0) => {
    if (!evalChartState) return
    evalChartState.timeElapsed = Number.isFinite(timeElapsed) ? timeElapsed : 0
    drawEvalChart(evalChartState)
}

export const loadEvalChart = (chartData: number[][] = [], chartMetadata: EvalChartMetadata, duration: number) => {
    cleanupEvalChart()
    const canvas = document.getElementById(evalChartId) as HTMLCanvasElement | null
    if (!canvas || (chartData[0]?.length ?? 0) <= 0) return

    const state: EvalChartState = {
        canvas,
        offscreenCanvas: document.createElement("canvas"),
        chartData,
        chartLength: chartData[0]?.length ?? 0,
        chartMetadata,
        durationSeconds: Math.max(duration / 1000, 0),
        timeElapsed: Audio.player?.currentTime ?? 0,
        onTimeUpdate: () => renderEvalChartProgress(Audio.player?.currentTime),
        onPointerDown: (evt: PointerEvent) => {
            const rect = canvas.getBoundingClientRect()
            const position = rect.width > 0 ? (evt.clientX - rect.left) / rect.width : 0
            const clickedTime = Math.max(Math.min(position, 1), 0) * state.durationSeconds
            if (!Number.isFinite(clickedTime)) return
            renderEvalChartProgress(clickedTime)
            Audio.player?.setCurrentTime(clickedTime)
        }
    }

    evalChartState = state
    canvas.addEventListener("pointerdown", state.onPointerDown)
    Audio.player?.media?.addEventListener("timeupdate", state.onTimeUpdate)
    drawEvalChart(state)
}
